import { useEffect } from 'react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { useSplit } from '../components/SplitView';
import { AgentCallsPanel } from '../components/AgentCallsPanel';

export type VoiceAgentStat = {
  externalAgentId: string;
  name: string;
  calls: number;
  durationSec: number;
  cost: number;
};
export type AgentDashboardData = {
  totalAgents: number;
  syncedAgents: number;
  totalCalls: number;
  /** True when the 30-day window held more than the fetch cap (1000) — the UI
   *  shows the count as "N+". */
  totalCallsCapped?: boolean;
  totalDurationSec: number;
  totalCost: number;
  avgDurationSec: number;
  windowDays: number;
  perAgent: VoiceAgentStat[];
};

// Module-level (not a ref): SplitView remounts the dashboard every time the
// panel toggles open/closed, which would reset a ref and re-trigger auto-open —
// making the panel impossible to close. A module flag survives the remount, so
// we auto-open exactly once per full page load and the close (X) sticks.
let didAutoOpen = false;

/** Admin dashboard data + the split-panel opener. Owns the auto-open-once
 *  behaviour so the page component is left as pure rendering. */
export function useAgentDashboard() {
  const crud = useClientQueries(schema);
  const split = useSplit();
  const q = crud.$procs.agentDashboard.useQuery(undefined, { staleTime: 60_000, retry: false });
  const data = q.data as AgentDashboardData | undefined;

  function openAssistant(s: VoiceAgentStat) {
    split.open({
      direction: 'horizontal',
      defaultSize: 45,
      content: (
        <AgentCallsPanel
          key={s.externalAgentId}
          externalAgentId={s.externalAgentId}
          name={s.name}
        />
      ),
    });
  }

  // Auto-open the first assistant's panel once per page load. Sets the flag
  // BEFORE opening so the remount the open triggers doesn't re-fire it.
  useEffect(() => {
    if (didAutoOpen) return;
    const first = data?.perAgent[0];
    if (first) {
      didAutoOpen = true;
      openAssistant(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return { data, isLoading: q.isLoading, isError: q.isError, error: q.error, openAssistant };
}
